import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { match } from 'react-router-dom';
import moment from 'moment';
import { CommunicationsHistoryForm, MasksConfig, SendStatus } from 'types';

import HistorisationList from 'components/templates/historisation-list';
import Layout from 'components/templates/layout';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';
import {
  historicCommunicationActions,
  historicCommunicationSelectors,
  StationsSelectors,
  SituationsCommunicationsActions,
  SituationsCommunicationsSelectors,
  MasksConfigActions,
  MasksConfigSelectors,
  UserInfoSelectors,
  SendStatutesActions,
} from 'common/config/store';
import HistorisationFilterForm from 'components/templates/form-historisation-list';
import { useIntl } from 'common/config/i18n';
import ItemLink from 'components/molecules/item-link';
import { useTheme } from 'common/styling/theme';

const { StyledListWrapper, StyledListTitleWrapper } = StyledListUtil;

interface Params {
  serviceName?: string;
  circulationDate?: string;
}

interface Props {
  match: match<Params>;
}

export interface FilterOrderParams {
  name?:
    | 'SentDateTime'
    | 'ProcessName'
    | 'SituationName'
    | 'Sender'
    | 'ServiceName'
    | 'ServiceDate'
    | 'Station'
    | 'CustomerSelection'
    | 'Compensation';
  order?: 'DESC' | 'ASC';
}

const Historisation: React.FC<Props> = (props) => {
  const { t, currentLocale } = useIntl();
  const { theme } = useTheme();
  const dispatch = useDispatch();

  const [filterValues, setFilterValues] = useState<CommunicationsHistoryForm>();
  const [filterOrder, setFilterOrder] = useState<FilterOrderParams>({
    name: 'SentDateTime',
    order: 'DESC',
  });
  const [communicationHistoryModalVisible, setCommunicationHistoryModalVisible] = useState(false);
  const [sendStatutes, setSendStatutes] = useState<SendStatus[]>([]);

  const historicCommData = useSelector(historicCommunicationSelectors.dataSelector) || {};
  const historicCommStatus = useSelector(historicCommunicationSelectors.statusSelector);
  const originStations = useSelector(StationsSelectors.originStationsSelector);
  const masksConfig: MasksConfig = useSelector(MasksConfigSelectors.masksConfigSelector);
  const senders = useSelector(historicCommunicationSelectors.sendersSelector);
  const situations = useSelector(SituationsCommunicationsSelectors.situationsCommunicationsSelector);
  const actifUserServiceTypes = useSelector(UserInfoSelectors.getActifUserServiceTypesSelector);

  const { serviceName, circulationDate } = props.match.params;

  useEffect(() => {
    dispatch(
      SendStatutesActions.fetchSendStatus((result) => {
        if (!result.cancelled && !result.error) {
          if (result.data && result.data.length > 0) {
            setSendStatutes(result.data);
          }
        }
      })
    );
  }, [dispatch]);

  useEffect(() => {
    setFilterValues({
      communicationStartDate: moment().subtract(10, 'days').format('YYYY-MM-DD'),
      communicationEndDate: moment().format('YYYY-MM-DD'),
    });

    dispatch(SituationsCommunicationsActions.fetchCommunicationsSituations());
    dispatch(MasksConfigActions.fetchMasksConfig());
    dispatch(historicCommunicationActions.fetchHistoricCommunicationSenders());
    dispatch(
      historicCommunicationActions.fetchHistoricCommunication({
        isDifferedCommunication: false,
        serviceName: serviceName || undefined,
        communicationStartDate: circulationDate ? undefined : moment().subtract(10, 'days').format('YYYY-MM-DD'),
        communicationEndDate: circulationDate ? undefined : moment().format('YYYY-MM-DD'),
        arrivalTime: circulationDate ? moment(circulationDate).format('YYYY-MM-DD') : undefined,
        departureTime: circulationDate ? moment(circulationDate).format('YYYY-MM-DD') : undefined,
        pageNumber: 1,
        maxResultPage: 20,
        parameterToSort: 'SentDateTime',
        ascDesc: 'DESC',
      })
    );
  }, [dispatch, currentLocale, serviceName, circulationDate]);

  return (
    <div>
      <Layout />
      <StyledListWrapper>
        <StyledMainTitle>{t('filtre', 'HISTORISATION_TABLE')}</StyledMainTitle>
        <StyledListTitleWrapper style={{ marginBottom: 8, alignItems: 'end', justifyContent: 'right' }}>
          <ItemLink
            to="/historisation-pending"
            name={t('filtre', 'HISTORISATION_PENDING_LINK')}
            style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: theme.colors.primary.main,
              textDecoration: 'none',
            }}
          />
        </StyledListTitleWrapper>
        <HistorisationFilterForm
          situations={situations}
          senders={senders}
          sendStatutes={sendStatutes}
          originStations={originStations}
          data={historicCommData}
          circulationValues={{ serviceName, circulationDate }}
          filterOrder={filterOrder}
          isCommunicationHistoryModalVisible={communicationHistoryModalVisible}
          actifUserServiceTypes={actifUserServiceTypes}
          setFilterValues={setFilterValues}
          setFilterOrder={setFilterOrder}
        />
        {historicCommStatus === 'success' && (
          <HistorisationList
            data={historicCommData}
            filterValues={filterValues}
            channels={masksConfig}
            filterOrder={filterOrder}
            modalVisible={communicationHistoryModalVisible}
            setFilterOrder={setFilterOrder}
            setModalVisible={setCommunicationHistoryModalVisible}
          />
        )}
      </StyledListWrapper>
    </div>
  );
};

export default Historisation;
